import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAttachmentById, getAttachmentUrl } from '@connect-x-app/hybrid-data-access';
import { useTimeZone } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import {
  ApiError,
  AttachmentData,
  AttachmentType,
  HttpStatus,
  MessageMention,
  QueryKeys,
} from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { isPublicMessage, normalizeAttachmentType, toBase64, useInternetConnection } from '@connect-x-app/shared-utils';
import { ChatMessage } from '@connect-x-app/shared-view-models';
import { Avatar, Icon, MessageStatusIcon, Spinner, toastError } from '@connect-x-app/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import classnames from 'classnames';
import { clsx } from 'clsx';

import { MessageBubbleText, MessageStatusWrapper, UserInfo } from './components';

import './messageBubble.scss';

type Props = {
  currentMessage: ChatMessage;
  disabled?: boolean;
  isFocused: boolean;
  nextMessage: ChatMessage;
  onClickAttachment: (attachment: AttachmentData) => void;
  onDelete: (message: ChatMessage) => void;
  onRetry: (message: ChatMessage, pendingImage: File | null) => void;
  pendingFile: File | null;
  previousMessage: ChatMessage | undefined;
  setActiveMention: (mention: MessageMention) => void;
  showMessageInfo: () => void;
};

export const MessageBubble = ({
  currentMessage,
  disabled = false,
  isFocused,
  nextMessage,
  onClickAttachment,
  onDelete,
  onRetry,
  pendingFile,
  previousMessage,
  setActiveMention,
  showMessageInfo,
}: Props) => {
  const { t } = useTranslation();
  const { formatTimeInTimeZone } = useTimeZone();
  const queryClient = useQueryClient();
  const { attachments, failed, isMe: messageIsMe, mentions, pending, status, text, userInfo } = currentMessage;
  const publicMessage = isPublicMessage(currentMessage.visibility);
  const nextMessageBySameUser = nextMessage?.userInfo?.id === currentMessage?.userInfo?.id;
  const isMe = messageIsMe || pending;
  const timestamp = currentMessage?.createdAt ? formatTimeInTimeZone(currentMessage.createdAt) : null;
  const hasAvatar = (previousMessage?.userInfo?.id !== currentMessage?.userInfo?.id || !!previousMessage?.system) && !isMe;
  const [pendingFileBase64, setPendingFileBase64] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [attachmentError, setAttachmentError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isInternetConnectionOnline } = useInternetConnection();

  const onClickRetry = () => {
    if (currentMessage) {
      onRetry(currentMessage, pendingFile);
    }
  };

  const onClickDelete = () => {
    if (currentMessage) {
      onDelete(currentMessage);
    }
  };

  useEffect(() => {
    if (isInternetConnectionOnline) {
      setAttachmentError(false);
    }
  }, [isInternetConnectionOnline]);

  useEffect(() => {
    const getFailedImage = async (pendingImage: File) => {
      const base64 = await toBase64(pendingImage);
      setPendingFileBase64(base64);
    };

    if (pendingFile) {
      getFailedImage(pendingFile);
    }
  }, [pendingFile]);

  const showMessageActionsButton = () => {
    setIsHovered(isMe);
  };

  const hideMessageActionsButton = () => {
    setIsHovered(false);
  };

  const onClickMessageInfo = () => {
    setIsHovered(false);
    showMessageInfo();
  };

  const onClickImage = (attachment: AttachmentData) => {
    if (!attachmentError) {
      onClickAttachment(attachment);
    } else {
      toastError(t('CHAT.ATTACHMENT.FILE_NOT_ACCESSIBLE'));
    }
  };

  const onClickMessageAttachment = async (attachment: AttachmentData) => {
    setIsLoading(true);
    try {
      const data = await queryClient.fetchQuery({
        queryFn: () => getAttachmentById(attachment),
        queryKey: [QueryKeys.GET_ATTACHMENT, attachment.id],
      });
      const base64 = await toBase64(new File([data], attachment.name, { type: normalizeAttachmentType(attachment.type) }));
      onClickAttachment({ ...attachment, data: base64 });
    } catch (e: unknown) {
      const errorContent =
        (e as ApiError)?.statusCode === HttpStatus.FORBIDDEN
          ? t('CHAT.ATTACHMENT.FILE_NOT_ACCESSIBLE')
          : t('CHAT.ATTACHMENT.FAILED_TO_LOAD_FILE');
      toastError(errorContent);
    } finally {
      setIsLoading(false);
    }
  };

  const renderAttachments = (attachments: AttachmentData[]) => {
    return attachments.map(attachment => {
      const normalizedType = normalizeAttachmentType(attachment.type);
      if (normalizedType.startsWith('image')) {
        return (
          <button
            className="message-bubble-image-button-wrapper"
            key={attachment.id ?? attachment.name}
            onClick={() => onClickImage(attachment)}
          >
            {attachmentError ? (
              renderAttachmentWebPreview(AttachmentType.IMAGE, attachment.name)
            ) : (
              <img
                alt={t('CHAT.ATTACHMENT.FAILED_TO_LOAD_IMAGE')}
                crossOrigin="anonymous"
                onError={() => setAttachmentError(true)}
                src={attachment.id ? getAttachmentUrl(attachment) : attachment.data}
              />
            )}
          </button>
        );
      }

      if (normalizedType.endsWith('pdf')) {
        return (
          <button
            className="message-bubble-image-button-wrapper"
            disabled={isLoading}
            key={attachment.id ?? attachment.name}
            onClick={() => onClickMessageAttachment(attachment)}
          >
            {isLoading ? <Spinner /> : renderAttachmentWebPreview(AttachmentType.PDF, attachment.name)}
          </button>
        );
      }
      return null;
    });
  };

  const renderFiles = () => {
    if (attachments.length > 0) {
      const attachmentsWithContext: AttachmentData[] = attachments.map(a => ({
        ...a,
        channelId: currentMessage.channelId,
        clientMessageId: currentMessage.externalId ?? currentMessage.id,
        roomId: currentMessage.room,
      }));
      return renderAttachments(attachmentsWithContext);
    }

    if (pendingFile) {
      if (pendingFileBase64.startsWith('data:application/pdf')) {
        return renderAttachments([
          { channelId: '', clientMessageId: '', id: '', name: pendingFile.name, roomId: '', type: 'pdf' },
        ]);
      }

      return renderAttachments([
        {
          channelId: '',
          clientMessageId: '',
          data: pendingFileBase64,
          id: '',
          name: pendingFile.name,
          roomId: '',
          type: 'image',
        },
      ]);
    }

    return [];
  };

  return (
    <MessageStatusWrapper
      isMe={isMe}
      messageStatus={status}
      nextMessageBySameUser={nextMessageBySameUser}
      onClickDelete={onClickDelete}
      onClickRetry={onClickRetry}
    >
      {hasAvatar && <UserInfo user={userInfo} />}
      <div
        className={classnames('message-bubble-container', { disabled })}
        onMouseEnter={showMessageActionsButton}
        onMouseLeave={hideMessageActionsButton}
        role={'presentation'}
      >
        {hasAvatar && <Avatar icon={userInfo.icon} isDisabled />}
        <div
          className={classnames('message-bubble-content-container', {
            'by-me': isMe,
            'has-attachment': (attachments && attachments.length > 0) || !!pendingFile,
            'has-avatar': hasAvatar,
            'is-focused': isFocused,
          })}
        >
          {renderFiles()}
          <MessageBubbleText
            disabled={disabled}
            isMe={isMe}
            mentions={mentions ?? []}
            setActiveMention={setActiveMention}
            text={text}
            withStatus={isMe && !pending && !failed}
          />
          <div className="message-bubble-meta">
            <Text className="text-date" color={isMe ? COLORS.zulu.$08 : COLORS.zulu.$10} size="XXS">
              {timestamp ?? ''}
            </Text>
            {isMe && publicMessage ? <MessageStatusIcon status={status} /> : null}
          </div>
        </div>
        <div>
          {!disabled ? (
            <Icon
              className={clsx('info-icon', {
                hidden: !isHovered,
              })}
              colorName={COLORS.yuma.$12}
              name="circularInfoOutline"
              onClick={onClickMessageInfo}
              size={2}
            />
          ) : null}
        </div>
      </div>
    </MessageStatusWrapper>
  );
};

const renderAttachmentWebPreview = (type: AttachmentType, name: string) => {
  return (
    <div className="attachment-preview-container">
      <Icon
        colorName={COLORS.zulu.$08}
        name={
          type === AttachmentType.PDF
            ? 'docfolderDocumentFormatPdfTemplateOutline'
            : 'docfolderDocumentFormatImageTemplateOutline'
        }
        size={4.8}
      />
      <Text>{name}</Text>
    </div>
  );
};
